import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import DatePicker from '../../components/ui/DatePicker';
import Button from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import StepProgress from '../../components/candidates/StepProgress';
import WithdrawDialog from '../../components/candidates/WithdrawDialog';
import Can from '../../auth/Can';
import { listCandidates, deleteCandidate, withdrawCandidate, exportCandidates } from '../../api/candidates';
import { listWorkflowCategories, listStepDefinitions } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';
import { saveBlobResponse } from '../../utils/download';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

export default function CandidatesList() {
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [currentStep, setCurrentStep] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [page, setPage] = useState(1);
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  const stepDefinitionsQuery = useQuery({ queryKey: ['step-definitions'], queryFn: listStepDefinitions });

  const params = {
    search: search || undefined,
    status: status || undefined,
    workflow_category_id: categoryId || undefined,
    current_step: currentStep || undefined,
    created_from: createdFrom || undefined,
    created_to: createdTo || undefined,
    page,
  };

  const candidatesQuery = useQuery({ queryKey: ['candidates', params], queryFn: () => listCandidates(params) });

  const countQueries = useQueries({
    queries: STATUS_TABS.filter((t) => t.value).map((t) => ({
      queryKey: ['candidates-count', t.value],
      queryFn: () => listCandidates({ status: t.value, per_page: 1 }),
      select: (data) => data.total,
    })),
  });

  const withdrawMutation = useMutation({
    mutationFn: ({ id, reason }) => withdrawCandidate(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      pushToast({ type: 'success', message: 'Candidate withdrawn.' });
      setWithdrawTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not withdraw candidate.' });
      setWithdrawTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCandidate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      pushToast({ type: 'success', message: 'Candidate deleted.' });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete candidate.' });
      setDeleteTarget(null);
    },
  });

  const handleExport = async () => {
    try {
      const response = await exportCandidates(params);
      saveBlobResponse(response, 'candidates.csv');
    } catch {
      pushToast({ type: 'error', message: 'Could not export candidates.' });
    }
  };

  const columns = [
    {
      key: 'reference_no',
      label: 'Reference No',
      render: (row) => (
        <Link to={`/candidates/${row.id}`} className="font-medium text-primary hover:underline">
          {row.reference_no}
        </Link>
      ),
    },
    { key: 'full_name', label: 'Full Name' },
    { key: 'nationality', label: 'Nationality' },
    { key: 'category', label: 'Category', render: (row) => <CategoryBadge category={row.category} /> },
    {
      key: 'current_step',
      label: 'Current Step',
      render: (row) =>
        row.current_step ? (
          <div className="w-40">
            <StepProgress
              current={row.current_step.sort_order}
              total={row.current_step.total_steps}
              label={row.current_step.title}
              percent={row.current_step.progress_percent}
            />
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'created_at', label: 'Registered', render: (row) => new Date(row.created_at).toLocaleDateString() },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Link to={`/candidates/${row.id}`} className="text-primary hover:underline">
            View
          </Link>
          <Can perm="candidates.withdraw">
            {row.status !== 'withdrawn' && row.status !== 'rejected' && row.status !== 'hired' && (
              <button onClick={() => setWithdrawTarget(row)} className="text-gray-600 hover:underline">
                Withdraw
              </button>
            )}
          </Can>
          <Can perm="candidates.delete">
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Candidates"
        actions={
          <>
            <Can perm="candidates.export">
              <Button variant="secondary" onClick={handleExport}>
                Export CSV
              </Button>
            </Can>
            <Can perm="candidates.create">
              <Link to="/candidates/new">
                <Button>Register Candidate</Button>
              </Link>
            </Can>
          </>
        }
      />

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {STATUS_TABS.map((tab, i) => (
          <button
            key={tab.value}
            onClick={() => {
              setStatus(tab.value);
              setPage(1);
            }}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              status === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.value && countQueries[i - 1]?.data !== undefined && (
              <span className="ml-1.5 text-xs text-gray-400">({countQueries[i - 1].data})</span>
            )}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          placeholder="Search name, reference, passport, contact…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-72"
        />
        <Select
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
            setPage(1);
          }}
          className="w-56"
        >
          <option value="">All categories</option>
          {(categoriesQuery.data || []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          value={currentStep}
          onChange={(e) => {
            setCurrentStep(e.target.value);
            setPage(1);
          }}
          className="w-56"
        >
          <option value="">Any current step</option>
          {(stepDefinitionsQuery.data || []).map((s) => (
            <option key={s.code} value={s.code}>
              {s.title}
            </option>
          ))}
        </Select>
        <DatePicker value={createdFrom} onChange={(e) => { setCreatedFrom(e.target.value); setPage(1); }} className="w-40" />
        <DatePicker value={createdTo} onChange={(e) => { setCreatedTo(e.target.value); setPage(1); }} className="w-40" />
      </div>

      <Table
        columns={columns}
        data={candidatesQuery.data?.data || []}
        isLoading={candidatesQuery.isLoading}
        pagination={candidatesQuery.data}
        onPageChange={setPage}
        emptyMessage="No candidates found."
      />

      <WithdrawDialog
        open={!!withdrawTarget}
        onClose={() => setWithdrawTarget(null)}
        onConfirm={(reason) => withdrawMutation.mutate({ id: withdrawTarget.id, reason })}
        loading={withdrawMutation.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete candidate"
        message={`Delete ${deleteTarget?.full_name} (${deleteTarget?.reference_no})? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
